import { all, call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import * as loginActions from "~/login/actions";
import { AgEventAPI, SetupAPI } from "@ai360/core";
import { Toolset } from "@ai360/core";

import { GeometryUtils } from "@ai360/core";
import * as esriProjection from "@arcgis/core/geometry/projection";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import * as actions from "./actions";
import * as selectors from "./selectors";
import { actions as messagingActions } from "../../../messaging";
import { getTheUserGuid } from "~/login/selectors";
import { actions as notificationActions } from "~/notifications";
import { eventsModels } from "~/recs-events";
import { RecAPI, ImportAPI, FieldAPI } from "@ai360/core";
import { importWizardSelectors } from "~/action-panel/components/import-module";
import { mapActions } from "~/map";

const _requestUnitLists = function (userGuid) {
    return new Promise((resolve) => {
        const promises = [];
        promises.push(SetupAPI.getUnitAgBytesList(userGuid, "name", "Area"));
        promises.push(SetupAPI.getUnitAgBytesList(userGuid, "name", "Length"));
        Promise.all(promises).then((units) => {
            resolve({
                areaUnits: units[0],
                lengthUnits: units[1],
            });
        });
    });
};

const checkForJdPortalAccess = function* (action) {
    if (!action.payload?.orgLevelGuid || !action.payload?.customerGuid) {
        return;
    }
    try {
        const response = yield RecAPI.checkForOrgIdAndAccessToken(
            action.payload.orgLevelGuid,
            action.payload.customerGuid
        );
        if (response.statusCode == 200) {
            yield put(actions.setUserJdPortalAccess(true));
        } else {
            yield put(actions.setUserJdPortalAccess(false));
        }
    } catch (error) {
        yield put(notificationActions.apiCallError(error));
    }
};

const checkForProposedBoundaryRequest = function* (action) {
    if (action.payload?.borderForProposedBoundary == null) {
        return;
    }
    const importFileGuids = yield select(importWizardSelectors.getImportFileGuidList);
    try {
        yield put(mapActions.setIsLoading(true));
        const currentGeometries = yield select(selectors.getFieldEditGeometries);
        const response = yield ImportAPI.getProposedBoundary({
            boundingBox: JSON.stringify(action.payload.borderForProposedBoundary),
            importFileGuids: importFileGuids,
        });
        yield put(actions.generateProposedBoundarySuccess(response));
        const geometry = GeometryUtils.wktToGeometry(response);
        const projected = esriProjection.project(
            geometry,
            new SpatialReference({ wkid: FieldAPI.spatialReference })
        );
        yield put(actions.setFieldEditGeometries([...currentGeometries, projected]));
        yield put(actions.setGeneratedShrinkwrappedBoundary(true));
    } catch (error) {
        yield put(actions.generateProposedBoundaryFailed({ error }));
        yield put(notificationActions.apiCallError(error, action));
    } finally {
        yield put(mapActions.setIsLoading(false));
    }
};

const checkIfFieldEditGeometriesChanged = function* (action) {
    if (action.payload.geometries == null) {
        return;
    }
    const toolset = yield select(selectors.getActiveToolset);
    const isChanged = yield select(selectors.getFieldEditsChanged);
    const isLoaded = yield select(selectors.getFieldEditsLoaded);

    if (
        toolset !== Toolset.DEFAULT &&
        !isChanged &&
        (action.payload.geometries.length > 0 || isLoaded)
    ) {
        if (!isLoaded) {
            yield put(actions.setFieldEditsLoaded(true));
        } else {
            yield put(actions.setFieldEditsChanged(true));
        }
    } else if (toolset === Toolset.DEFAULT && (isChanged || isLoaded)) {
        yield put(actions.setFieldEditsChanged(false));
        yield put(actions.setFieldEditsLoaded(false));
    }
};

const getStarted = function* (action) {
    yield call(getUnitLists, action);
};

const getUnitLists = function* (action) {
    try {
        const unitLists = yield call(_requestUnitLists, action.theUser.userGuid);
        yield put(actions.unitsFetchSucceeded(unitLists.areaUnits, unitLists.lengthUnits));
    } catch (e) {
        yield put(actions.unitsFetchFailed(e.message));
    }
};

const messageSubscriptions = function* () {
    yield put(
        messagingActions.subscribe(1000, {
            eventName: "processCounterAction",
            actionAccumulate: actions.processCounterMessage,
        })
    );
};

const onFetchDefaultZoneUserGridSettings = function* (action) {
    const userGuid = yield select(getTheUserGuid);
    let userGridSettings;
    try {
        userGridSettings = yield call(AgEventAPI.getDefaultUserGridSettings, userGuid);
    } catch (err) {
        yield put(notificationActions.apiCallError(err, action));
        return;
    }

    if (userGridSettings.areaDimension == null || userGridSettings.areaDimension === "") {
        userGridSettings.areaDimension = eventsModels.AreaDimension.AREA;
    }
    if (userGridSettings.height == null) {
        userGridSettings.height = 330;
    }
    if (userGridSettings.width == null) {
        userGridSettings.width = 330;
    }
    if (userGridSettings.gridSize == null) {
        userGridSettings.gridSize = 2.5;
    }
    if (userGridSettings.rotation == null) {
        userGridSettings.rotation = 0;
    }
    if (userGridSettings.offsetX == null) {
        userGridSettings.offsetX = 0;
    }
    if (userGridSettings.offsetY == null) {
        userGridSettings.offsetY = 0;
    }

    yield put(actions.fetchDefaultZoneUserGridSettingsSucceed(userGridSettings));
};

export function* mapToolsSaga() {
    yield all([
        messageSubscriptions(),
        takeLatest(
            actions.FETCH_DEFAULT_ZONE_USER_GRID_SETTINGS,
            onFetchDefaultZoneUserGridSettings
        ),
        takeLatest(loginActions.SET_USER_INFO_COMPLETE, getStarted),
        takeEvery(actions.SET_FIELD_EDIT_GEOMETRIES, checkIfFieldEditGeometriesChanged),
        takeEvery(actions.getUserJdPortalAccess, checkForJdPortalAccess),
        takeEvery(actions.generateProposedBoundary, checkForProposedBoundaryRequest),
    ]);
}
